import {
  Plus, Minus, Trash2 
} from 'lucide-react';
import type { Recipe } from '@/features/recipes/types';
import type { Menu } from '@/features/menus/types';
import type { CatalogItem } from '@/features/catalog/types';
import type {
  CreateRequisitionLine, RecipeSelection, MenuSelection 
} from '../../types';

interface MenuSelectorProps {
  readonly menus: Menu[];
  readonly selectedMenus: MenuSelection[];
  readonly onAdd: (menu: Menu) => void;
  readonly onRemove: (menuId: string) => void;
  readonly onUpdatePax: (menuId: string, pax: number) => void;
  readonly isLoading: boolean;
}

export function MenuSelector({
  menus,
  selectedMenus,
  onAdd,
  onRemove,
  onUpdatePax,
  isLoading,
}: MenuSelectorProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`menu-skel-${i.toString()}`} className="h-16 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected menus */}
      {selectedMenus.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Selected Menus</p>
          {selectedMenus.map((sel) => {
            const menu = menus.find((m) => m.menuId === sel.menuId);
            return (
              <div
                key={sel.menuId}
                className="flex items-center justify-between p-3 border rounded-lg bg-primary/5"
              >
                <div>
                  <p className="font-medium">{sel.menuName}</p>
                  <p className="text-sm text-muted-foreground">
                    {menu?.totalItems ?? 0} items • {menu?.venueName}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdatePax(sel.menuId, sel.paxCount - 10)}
                      className="p-1 rounded hover:bg-muted"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      value={sel.paxCount}
                      onChange={(e) => onUpdatePax(sel.menuId, parseInt(e.target.value) || 1)}
                      className="w-16 text-center rounded border px-2 py-1 text-sm"
                      min={1}
                    />
                    <span className="text-sm text-muted-foreground">pax</span>
                    <button
                      type="button"
                      onClick={() => onUpdatePax(sel.menuId, sel.paxCount + 10)}
                      className="p-1 rounded hover:bg-muted"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(sel.menuId)}
                    className="p-1 rounded text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Available menus */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Available Menus</p>
        {menus.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No menus found</p>
        ) : (
          <div className="grid gap-2 max-h-48 overflow-y-auto">
            {menus
              .filter((m) => !selectedMenus.some((s) => s.menuId === m.menuId))
              .map((menu) => (
                <button
                  key={menu.menuId}
                  type="button"
                  onClick={() => onAdd(menu)}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 text-left transition-colors"
                >
                  <div>
                    <p className="font-medium">{menu.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {menu.totalItems} items • {menu.venueName} • {menu.type}
                    </p>
                  </div>
                  <Plus className="h-5 w-5 text-muted-foreground" />
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface RecipeSelectorProps {
  readonly recipes: Recipe[];
  readonly selectedRecipes: RecipeSelection[];
  readonly onAdd: (recipe: Recipe) => void;
  readonly onRemove: (recipeId: string) => void;
  readonly onUpdatePortions: (recipeId: string, portions: number) => void;
  readonly isLoading: boolean;
}

export function RecipeSelector({
  recipes,
  selectedRecipes,
  onAdd,
  onRemove,
  onUpdatePortions,
  isLoading,
}: RecipeSelectorProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`recipe-skel-${i.toString()}`} className="h-16 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected recipes */}
      {selectedRecipes.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Selected Recipes</p>
          {selectedRecipes.map((sel) => {
            const recipe = recipes.find((r) => r.recipeId === sel.recipeId);
            return (
              <div
                key={sel.recipeId}
                className="flex items-center justify-between p-3 border rounded-lg bg-primary/5"
              >
                <div>
                  <p className="font-medium">{sel.recipeName}</p>
                  <p className="text-sm text-muted-foreground">
                    {recipe?.ingredients.length ?? 0} ingredients • {recipe?.category}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdatePortions(sel.recipeId, sel.portions - 5)}
                      className="p-1 rounded hover:bg-muted"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      value={sel.portions}
                      onChange={(e) => onUpdatePortions(sel.recipeId, parseInt(e.target.value) || 1)}
                      className="w-16 text-center rounded border px-2 py-1 text-sm"
                      min={1}
                    />
                    <span className="text-sm text-muted-foreground">portions</span>
                    <button
                      type="button"
                      onClick={() => onUpdatePortions(sel.recipeId, sel.portions + 5)}
                      className="p-1 rounded hover:bg-muted"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(sel.recipeId)}
                    className="p-1 rounded text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Available recipes */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Available Recipes</p>
        {recipes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No recipes found</p>
        ) : (
          <div className="grid gap-2 max-h-48 overflow-y-auto">
            {recipes
              .filter((r) => !selectedRecipes.some((s) => s.recipeId === r.recipeId))
              .map((recipe) => (
                <button
                  key={recipe.recipeId}
                  type="button"
                  onClick={() => onAdd(recipe)}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 text-left transition-colors"
                >
                  <div>
                    <p className="font-medium">{recipe.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {recipe.ingredients.length} ingredients • {recipe.category} •{' '}
                      {recipe.servings} servings
                    </p>
                  </div>
                  <Plus className="h-5 w-5 text-muted-foreground" />
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ManualItemSelectorProps {
  readonly items: CatalogItem[];
  readonly selectedItems: CreateRequisitionLine[];
  readonly onAdd: (item: CatalogItem) => void;
  readonly onRemove: (itemId: string) => void;
  readonly onUpdateQuantity: (itemId: string, quantity: number) => void;
  readonly isLoading: boolean;
}

export function ManualItemSelector({
  items,
  selectedItems,
  onAdd,
  onRemove,
  onUpdateQuantity,
  isLoading,
}: ManualItemSelectorProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`item-skel-${i.toString()}`} className="h-16 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected items */}
      {selectedItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Selected Items</p>
          {selectedItems.map((item) => (
            <div
              key={item.itemId}
              className="flex items-center justify-between p-3 border rounded-lg bg-primary/5"
            >
              <div>
                <p className="font-medium">{item.itemName}</p>
                <p className="text-sm text-muted-foreground">{item.sku}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onUpdateQuantity(item.itemId, item.quantity - 1)}
                    className="p-1 rounded hover:bg-muted"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      onUpdateQuantity(item.itemId, parseFloat(e.target.value) || 0.01)
                    }
                    className="w-20 text-center rounded border px-2 py-1 text-sm"
                    min={0.01}
                    step={0.1}
                  />
                  <span className="text-sm text-muted-foreground">{item.unit}</span>
                  <button
                    type="button"
                    onClick={() => onUpdateQuantity(item.itemId, item.quantity + 1)}
                    className="p-1 rounded hover:bg-muted"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(item.itemId)}
                  className="p-1 rounded text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available items */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Catalog Items</p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Search for items to add...
          </p>
        ) : (
          <div className="grid gap-2 max-h-48 overflow-y-auto">
            {items
              .filter((i) => !selectedItems.some((s) => s.itemId === i.itemId))
              .map((item) => (
                <button
                  key={item.itemId}
                  type="button"
                  onClick={() => onAdd(item)}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 text-left transition-colors"
                >
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.sku} • {item.category} • {item.unit}
                    </p>
                  </div>
                  <Plus className="h-5 w-5 text-muted-foreground" />
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
